import type { OnboardingRequirement, OnboardingRun, OnboardingStage, OnboardingTemplate } from '~/types/models';

export const DEFAULT_ONBOARDING_STAGES: OnboardingStage[] = [
    {
        id: 'welcome_sent',
        label: 'Welcome sent',
        description: 'The client has received their onboarding welcome and portal access.',
        order: 1,
    },
    {
        id: 'portal_activated',
        label: 'Portal activated',
        description: 'The primary client contact has accessed the portal.',
        order: 2,
    },
    {
        id: 'questionnaire_complete',
        label: 'Questionnaire complete',
        description: 'Required intake forms and supporting information are complete.',
        order: 3,
    },
    {
        id: 'contract_signed',
        label: 'Contract signed',
        description: 'The client has accepted the scope and signed the agreement.',
        order: 4,
    },
    {
        id: 'deposit_paid',
        label: 'Deposit paid',
        description: 'The initial invoice or payment requirement has been completed.',
        order: 5,
    },
    {
        id: 'kickoff_scheduled',
        label: 'Kickoff scheduled',
        description: 'The internal and client kickoff is booked.',
        order: 6,
    },
    {
        id: 'in_progress',
        label: 'In progress',
        description: 'The project has moved from onboarding into delivery.',
        order: 7,
    },
];

function commonRequirements(contractScope: 'client' | 'project'): OnboardingTemplate['requirements'] {
    return [
        {
            id: 'portal-activation',
            key: 'portal_activation',
            type: 'portal_activation',
            title: 'Activate client portal',
            description: 'The client signs in and confirms access to their portal.',
            stageId: 'portal_activated',
            linkedResourceType: 'client',
        },
        {
            id: 'contract-signature',
            key: 'contract_signature',
            type: 'contract',
            title: contractScope === 'client' ? 'Sign client agreement' : 'Sign project agreement',
            description: contractScope === 'client'
                ? 'The client reviews the agreement and signs before kickoff.'
                : 'The client reviews the project agreement and signs before kickoff.',
            stageId: 'contract_signed',
            linkedResourceType: 'contract',
            linkedResourceScope: contractScope,
        },
        {
            id: 'deposit-payment',
            key: 'deposit_payment',
            type: 'invoice',
            title: 'Pay deposit invoice',
            description: 'The client pays the deposit or first invoice before delivery starts.',
            stageId: 'deposit_paid',
            linkedResourceType: 'invoice',
        },
        {
            id: 'kickoff-call',
            key: 'kickoff_call',
            type: 'task',
            title: 'Schedule kickoff call',
            description: 'The team books the kickoff call and confirms next steps.',
            stageId: 'kickoff_scheduled',
            linkedResourceType: 'task',
        },
    ];
}

export const DEFAULT_ONBOARDING_TEMPLATES: OnboardingTemplate[] = [
    {
        id: 'general-client-onboarding',
        name: 'General client onboarding',
        description: 'A flexible onboarding flow for new clients.',
        category: 'general',
        stages: DEFAULT_ONBOARDING_STAGES,
        requirements: [
            {
                id: 'business-intake',
                key: 'business_intake',
                type: 'form',
                title: 'Complete business information',
                description: 'Gather company, contact, goals, and working preferences.',
                stageId: 'questionnaire_complete',
                linkedResourceType: 'form',
            },
            ...commonRequirements('client'),
        ],
        isDefault: true,
    },
    {
        id: 'website-project-onboarding',
        name: 'Website project onboarding',
        description: 'Collects the details needed to start a website or redesign project.',
        category: 'website',
        stages: DEFAULT_ONBOARDING_STAGES,
        requirements: [
            {
                id: 'website-intake',
                key: 'website_intake',
                type: 'form',
                title: 'Complete website intake',
                description: 'Gather goals, pages, hosting, domain, access, content, and competitor examples.',
                stageId: 'questionnaire_complete',
                linkedResourceType: 'form',
            },
            {
                id: 'asset-upload',
                key: 'website_assets',
                type: 'file_upload',
                title: 'Upload website assets',
                description: 'Collect logos, brand files, images, copy, and existing site assets.',
                stageId: 'questionnaire_complete',
                linkedResourceType: 'project',
            },
            ...commonRequirements('project'),
        ],
        isDefault: true,
    },
    {
        id: 'branding-project-onboarding',
        name: 'Branding project onboarding',
        description: 'Collects creative direction, assets, references, and sign-off requirements.',
        category: 'branding',
        stages: DEFAULT_ONBOARDING_STAGES,
        requirements: [
            {
                id: 'brand-questionnaire',
                key: 'brand_questionnaire',
                type: 'form',
                title: 'Complete brand questionnaire',
                description: 'Gather brand personality, audience, style preferences, references, and competitors.',
                stageId: 'questionnaire_complete',
                linkedResourceType: 'form',
            },
            {
                id: 'brand-assets',
                key: 'brand_assets',
                type: 'file_upload',
                title: 'Upload existing brand assets',
                description: 'Collect existing logos, guidelines, imagery, and campaign materials.',
                stageId: 'questionnaire_complete',
                linkedResourceType: 'project',
            },
            ...commonRequirements('project'),
        ],
        isDefault: true,
    },
];

export function resolveOnboardingTemplate(templateId?: string | null): OnboardingTemplate {
    return DEFAULT_ONBOARDING_TEMPLATES.find((template) => template.id === templateId)
        || DEFAULT_ONBOARDING_TEMPLATES[0]!;
}

export function buildOnboardingRunFromTemplate(input: {
    template: OnboardingTemplate;
    tenantId: string;
    clientId: string;
    clientName?: string;
    clientSlug?: string;
    projectId?: string;
    projectName?: string;
    projectSlug?: string;
    ownerId?: string;
    ownerName?: string;
    now?: Date;
}): Omit<OnboardingRun, 'id'> {
    const now = input.now || new Date();
    const firstStageId = input.template.stages[0]?.id || 'welcome_sent';
    const requirements: OnboardingRequirement[] = input.template.requirements.map((requirement) => {
        const scope = requirement.linkedResourceScope || 'project';
        return {
            ...requirement,
            linkedResourceScope: scope,
            status: 'pending',
            clientId: input.clientId,
            ...(input.projectId && scope !== 'client' ? { projectId: input.projectId } : {}),
        };
    });

    return {
        tenantId: input.tenantId,
        templateId: input.template.id,
        templateName: input.template.name,
        clientId: input.clientId,
        clientName: input.clientName,
        clientSlug: input.clientSlug,
        projectId: input.projectId,
        projectName: input.projectName,
        projectSlug: input.projectSlug,
        ownerId: input.ownerId,
        ownerName: input.ownerName,
        currentStageId: firstStageId,
        status: 'active',
        stages: input.template.stages,
        requirements,
        frictionScore: 0,
        frictionFlags: [],
        startedAt: now,
        createdAt: now,
        updatedAt: now,
    };
}
